"use client";

import type { ChatPlayerCompiledChat, ChatPlayerMessage, ChatPlayerPart } from "./chat-player-types";
import type { ChatPlayerPlaybackFrame } from "./chat-player-playback";
import type { ReactNode } from "react";
import type { StoreApi } from "zustand";

import { createContext, useContext, useState } from "react";
import { createStore, useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import {
  getChatPlayerForegroundPartKeysFromFrame,
  getChatPlayerPlaybackFrame,
  getChatPlayerToolStatusFromFrame,
  getChatPlayerVisibleMessagesFromFrame,
  getChatPlayerVisiblePartsFromFrame,
  getChatPlayerVisibleSpeechWordIndexFromFrame,
} from "./chat-player-playback";

type ChatPlayerPlaybackFrameInput = {
  timeMs: number;
  playbackKey?: number;
};

export interface ChatPlayerPlaybackStoreState {
  frame: ChatPlayerPlaybackFrame;
  playbackKey: number;
  actions: ChatPlayerPlaybackStoreActions;
}

export interface ChatPlayerPlaybackStoreActions {
  syncFrame: (frame: ChatPlayerPlaybackFrameInput) => void;
  syncToTime: (timeMs: number) => void;
}

export interface ChatPlayerPlaybackStoreApi {
  getState: () => ChatPlayerPlaybackStoreState;
  setState: StoreApi<ChatPlayerPlaybackStoreState>["setState"];
  subscribe: StoreApi<ChatPlayerPlaybackStoreState>["subscribe"];
  getInitialState: StoreApi<ChatPlayerPlaybackStoreState>["getInitialState"];
}

export type ChatPlayerPlaybackStore = ChatPlayerPlaybackStoreApi;

const ChatPlayerPlaybackStoreContext = createContext<ChatPlayerPlaybackStore | null>(null);

export function createChatPlayerPlaybackStore(chat: ChatPlayerCompiledChat, initialTimeMs: number) {
  const initialFrame = getChatPlayerPlaybackFrame(chat, initialTimeMs);

  return createStore<ChatPlayerPlaybackStoreState>()(set => {
    const actions: ChatPlayerPlaybackStoreActions = {
      syncFrame: frame =>
        set(previousState => {
          const nextFrame = getChatPlayerPlaybackFrame(chat, frame.timeMs);
          const playbackKey = frame.playbackKey ?? getRestartAwarePlaybackKey(previousState, nextFrame.timeMs);

          if (previousState.frame.timeMs === nextFrame.timeMs && previousState.playbackKey === playbackKey) {
            return previousState;
          }

          return {
            frame: nextFrame,
            playbackKey,
          };
        }),
      syncToTime: timeMs =>
        set(previousState => {
          const nextFrame = getChatPlayerPlaybackFrame(chat, timeMs);
          const playbackKey = getRestartAwarePlaybackKey(previousState, nextFrame.timeMs);

          if (previousState.frame.timeMs === nextFrame.timeMs && previousState.playbackKey === playbackKey) {
            return previousState;
          }

          return {
            frame: nextFrame,
            playbackKey,
          };
        }),
    };

    return {
      frame: initialFrame,
      playbackKey: 0,
      actions,
    };
  });
}

export function ChatPlayerPlaybackProvider({
  chat,
  children,
  initialTimeMs,
}: {
  chat: ChatPlayerCompiledChat;
  children: ReactNode;
  initialTimeMs: number;
}) {
  const [store] = useState(() => createChatPlayerPlaybackStore(chat, initialTimeMs));

  return <ChatPlayerPlaybackStoreContext.Provider value={store}>{children}</ChatPlayerPlaybackStoreContext.Provider>;
}

export function useChatPlayerPlaybackStore() {
  const store = useContext(ChatPlayerPlaybackStoreContext);

  if (!store) {
    throw new Error("ChatPlayer playback hooks must be used within ChatPlayerPlaybackProvider");
  }

  return store;
}

export function useChatPlayerPlaybackActions() {
  const store = useChatPlayerPlaybackStore();

  return useStore(store, state => state.actions);
}

export function useChatPlayerPlaybackKey() {
  const store = useChatPlayerPlaybackStore();

  return useStore(store, state => state.playbackKey);
}

export function useChatPlayerVisibleMessages(chat: ChatPlayerCompiledChat) {
  const store = useChatPlayerPlaybackStore();

  return useStore(
    store,
    useShallow(state => getChatPlayerVisibleMessagesFromFrame(chat, state.frame)),
  );
}

export function useChatPlayerVisibleParts(message: ChatPlayerMessage) {
  const store = useChatPlayerPlaybackStore();

  return useStore(
    store,
    useShallow(state => getChatPlayerVisiblePartsFromFrame(message, state.frame)),
  );
}

export function useChatPlayerForegroundPartKeys(chat: ChatPlayerCompiledChat, maxVisibleParts?: number) {
  const store = useChatPlayerPlaybackStore();
  const maxVisiblePartCount = getMaxVisiblePartCount(maxVisibleParts);

  return useStore(
    store,
    useShallow(state => {
      if (maxVisiblePartCount === null) {
        return null;
      }

      return getChatPlayerForegroundPartKeysFromFrame(chat, state.frame, maxVisiblePartCount);
    }),
  );
}

export function useChatPlayerSpeechVisibleWordIndex(
  messageId: string,
  part: Extract<ChatPlayerPart, { type: "speech" }>,
) {
  const store = useChatPlayerPlaybackStore();

  return useStore(store, state => getChatPlayerVisibleSpeechWordIndexFromFrame(state.frame, messageId, part));
}

export function useChatPlayerToolStatus(messageId: string, part: Extract<ChatPlayerPart, { type: "tool" }>) {
  const store = useChatPlayerPlaybackStore();

  return useStore(store, state => getChatPlayerToolStatusFromFrame(state.frame, messageId, part));
}

function getMaxVisiblePartCount(maxVisibleParts?: number) {
  if (maxVisibleParts === undefined || !Number.isFinite(maxVisibleParts)) {
    return null;
  }

  return Math.max(0, Math.floor(maxVisibleParts));
}

function getRestartAwarePlaybackKey(state: ChatPlayerPlaybackStoreState, nextTimeMs: number) {
  if (state.frame.timeMs >= 0 && nextTimeMs >= 0 && nextTimeMs < state.frame.timeMs) {
    return state.playbackKey + 1;
  }

  return state.playbackKey;
}
